import { useAtomValue } from "jotai";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Lightbox } from "@/components/Lightbox";
import { cx } from "@/lib/cx";
import { validatePhotoFile } from "@/modules/plus/photos/plus.photos.api";
import type { ResolvedPhoto } from "@/modules/plus/photos/plus.photos.atoms";
import { MAX_ACCESSORY_PHOTOS_PER_CODE } from "@/modules/plus/plus.schema";
import { atoms } from "@/state";

interface PhotoInlineProps {
  code: string;
  className?: string;
}

const PhotoThumb: React.FC<{
  photo: ResolvedPhoto;
  code: string;
  onOpen: () => void;
  onRemove?: () => void;
  busy?: boolean;
}> = ({ photo, code, onOpen, onRemove, busy }) => (
  <div className="relative">
    <button
      type="button"
      onClick={onOpen}
      className="block overflow-hidden rounded border border-gray-200 bg-white transition-transform hover:scale-110"
      title={photo.source === "uploaded" ? `Uploaded by ${photo.uploadedBy}` : "Baseline diagram"}
    >
      <img src={photo.url} alt={code} className="h-10 w-10 object-cover" />
    </button>
    {onRemove && (
      <button
        type="button"
        disabled={busy}
        onClick={onRemove}
        title="Remove this photo"
        className="absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-white text-[10px] leading-none text-red-600 shadow ring-1 ring-red-300 hover:bg-red-50 disabled:opacity-50"
      >
        ×
      </button>
    )}
  </div>
);

export const PhotoInline: React.FC<PhotoInlineProps> = ({ code, className }) => {
  const photos = useAtomValue(atoms.photos.byCode(code));
  const uploadMutation = useAtomValue(atoms.photos.upload);
  const removeMutation = useAtomValue(atoms.photos.remove);
  const signedIn = useAtomValue(atoms.auth.isSignedIn);
  const fileInput = useRef<HTMLInputElement>(null);

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const busy = uploadMutation.isPending || removeMutation.isPending;
  const uploadedCount = photos.filter((p) => p.source === "uploaded").length;
  const canUpload = uploadedCount < MAX_ACCESSORY_PHOTOS_PER_CODE;

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const validationError = validatePhotoFile(file);
    if (validationError) {
      toast.error(validationError);
      if (fileInput.current) {
        fileInput.current.value = "";
      }
      return;
    }
    try {
      await uploadMutation.mutateAsync({ code, file });
      toast.success(`Photo added for ${code}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      if (fileInput.current) {
        fileInput.current.value = "";
      }
    }
  };

  const onRemove = async (imagePath: string) => {
    if (!window.confirm(`Delete this photo for ${code}? This can't be undone.`)) {
      return;
    }
    try {
      await removeMutation.mutateAsync({ code, imagePath });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Remove failed");
    }
  };

  return (
    <div className={cx("flex flex-wrap items-center gap-2", className)}>
      {photos.map((p) => (
        <PhotoThumb
          key={p.imagePath ?? `baseline-${p.url}`}
          photo={p}
          code={code}
          busy={busy}
          onOpen={() => setLightboxUrl(p.url)}
          onRemove={
            signedIn && p.source === "uploaded" && p.imagePath
              ? () => onRemove(p.imagePath!)
              : undefined
          }
        />
      ))}
      {signedIn && (
        <>
          <button
            type="button"
            disabled={busy || !canUpload}
            onClick={() => fileInput.current?.click()}
            title={
              canUpload
                ? "Upload another photo"
                : `Max ${MAX_ACCESSORY_PHOTOS_PER_CODE} photos reached`
            }
            className="grid h-10 w-10 place-items-center rounded border border-dashed border-gray-300 text-lg leading-none text-gray-500 hover:border-blue-500 hover:text-blue-600 disabled:opacity-40"
          >
            +
          </button>
          <input
            ref={fileInput}
            type="file"
            // Explicit list keeps iOS Safari from offering HEIC by default;
            // the validatePhotoFile guard above is the real enforcement.
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onFile}
            className="hidden"
          />
        </>
      )}
      <Lightbox
        url={lightboxUrl}
        alt={`Photo for ${code}`}
        isOpen={lightboxUrl !== null}
        onOpenChange={(open) => {
          if (!open) {
            setLightboxUrl(null);
          }
        }}
      />
    </div>
  );
};
